import * as React from "react"
import {
  HelpCircle,
  Shield,
  Package,
  Contact,
  ChevronRight,
  Fan,
  Globe,
  Settings,
  MessageSquare,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarGroup,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar"
import { Link, useLocation } from "react-router-dom"
export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { isMobile, setOpenMobile, artistName, isSuperuser, sessionLoading } = useSidebar();
  const location = useLocation();
  const [isContactsOpen, setIsContactsOpen] = React.useState(false);
  const [isWebsiteOpen, setIsWebsiteOpen] = React.useState(false);
  const [isMessagesOpen, setIsMessagesOpen] = React.useState(false);
  const [isSalesOpen, setIsSalesOpen] = React.useState(false);
  const [isAutomationsOpen, setIsAutomationsOpen] = React.useState(false);

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const isContactsActive = () => {
    return location.pathname.startsWith("/contacts");
  };

  const isWebsiteActive = () => {
    return (
      location.pathname.startsWith("/sites") ||
      location.pathname.startsWith("/pages") ||
      location.pathname.startsWith("/files") ||
      location.pathname.startsWith("/support")
    );
  };

  const isMessagesActive = () => {
    return location.pathname.startsWith("/messages");
  };

  const isSalesActive = () => {
    return (
      location.pathname.startsWith("/sales") ||
      location.pathname.startsWith("/presales") ||
      location.pathname.startsWith("/products") ||
      location.pathname.startsWith("/discounts")
    );
  };

  const isAutomationsActive = () => {
    return location.pathname.startsWith("/automations");
  };

  // Auto-expand Contacts menu if on a contacts-related route
  React.useEffect(() => {
    if (location.pathname.startsWith("/contacts")) {
      setIsContactsOpen(true);
    }
  }, [location.pathname]);

  // Auto-expand Website menu if on a website-related route
  React.useEffect(() => {
    if (
      location.pathname.startsWith("/sites") ||
      location.pathname.startsWith("/pages") ||
      location.pathname.startsWith("/files") ||
      location.pathname.startsWith("/support")
    ) {
      setIsWebsiteOpen(true);
    }
  }, [location.pathname]);

  // Auto-expand Messages menu if on a messages-related route
  React.useEffect(() => {
    if (location.pathname.startsWith("/messages")) {
      setIsMessagesOpen(true);
    }
  }, [location.pathname]);

  // Auto-expand Sales menu if on a sales-related route
  React.useEffect(() => {
    if (
      location.pathname.startsWith("/sales") ||
      location.pathname.startsWith("/presales") ||
      location.pathname.startsWith("/products") ||
      location.pathname.startsWith("/discounts")
    ) {
      setIsSalesOpen(true);
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.pathname.startsWith("/automations")) {
      setIsAutomationsOpen(true);
    }
  }, [location.pathname]);

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  tooltip="Contacts" 
                  isActive={isContactsActive()}
                  onClick={() => setIsContactsOpen(!isContactsOpen)}
                >
                  <Contact />
                  <span>Contacts</span>
                  <ChevronRight 
                    className={`ml-auto size-4 transition-transform duration-200 ${
                      isContactsOpen ? "rotate-90" : ""
                    }`} 
                  />
                </SidebarMenuButton>
                {isContactsOpen && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={location.pathname === "/contacts"}
                      >
                        <Link to="/contacts" onClick={handleLinkClick}>
                          <span>List</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={location.pathname === "/contacts/search"}
                      >
                        <Link to="/contacts/search" onClick={handleLinkClick}>
                          <span>Search</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === "/contacts/import"}
                      >
                        <Link to="/contacts/import" onClick={handleLinkClick}>
                          <span>Import</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  tooltip="Messages" 
                  isActive={isMessagesActive()}
                  onClick={() => setIsMessagesOpen(!isMessagesOpen)}
                >
                  <MessageSquare />
                  <span>Messages</span>
                  <ChevronRight 
                    className={`ml-auto size-4 transition-transform duration-200 ${
                      isMessagesOpen ? "rotate-90" : ""
                    }`} 
                  />
                </SidebarMenuButton>
                {isMessagesOpen && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive("/messages/emails")}
                      >
                        <Link to="/messages/emails" onClick={handleLinkClick}>
                          <span>Emails</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive("/messages/sms")}
                      >
                        <Link to="/messages/sms/campaigns" onClick={handleLinkClick}>
                          <span>SMS</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton 
                  tooltip="Sales" 
                  isActive={isSalesActive()}
                  onClick={() => setIsSalesOpen(!isSalesOpen)}
                >
                  <Package />
                  <span>Sales</span>
                  <ChevronRight 
                    className={`ml-auto size-4 transition-transform duration-200 ${
                      isSalesOpen ? "rotate-90" : ""
                    }`} 
                  />
                </SidebarMenuButton>
                {isSalesOpen && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive("/products")}
                      >
                        <Link to="/products" onClick={handleLinkClick}>
                          <span>Products</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={
                          location.pathname === "/sales/checkouts" ||
                          location.pathname.startsWith("/sales/checkouts/")
                        }
                      >
                        <Link to="/sales/checkouts" onClick={handleLinkClick}>
                          <span>Checkouts</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={
                          location.pathname === "/sales/orders" ||
                          location.pathname.startsWith("/sales/orders/")
                        }
                      >
                        <Link to="/sales/orders" onClick={handleLinkClick}>
                          <span>Orders</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname.startsWith("/discounts")}
                      >
                        <Link to="/discounts" onClick={handleLinkClick}>
                          <span>Discounts</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Flows"
                  isActive={isAutomationsActive()}
                  data-testid="sidebar-flows"
                  onClick={() => setIsAutomationsOpen(!isAutomationsOpen)}
                >
                  <Fan />
                  <span>Flows</span>
                  <ChevronRight
                    className={`ml-auto size-4 transition-transform duration-200 ${
                      isAutomationsOpen ? "rotate-90" : ""
                    }`}
                  />
                </SidebarMenuButton>
                {isAutomationsOpen && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === "/automations"}
                      >
                        <Link to="/automations" onClick={handleLinkClick} data-testid="sidebar-automations">
                          <span>Templates</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === "/automations/runs"}
                      >
                        <Link to="/automations/runs" onClick={handleLinkClick}>
                          <span>Runs</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Website"
                  isActive={isWebsiteActive()}
                  onClick={() => setIsWebsiteOpen(!isWebsiteOpen)}
                >
                  <Globe />
                  <span>Website</span>
                  <ChevronRight
                    className={`ml-auto size-4 transition-transform duration-200 ${
                      isWebsiteOpen ? "rotate-90" : ""
                    }`}
                  />
                </SidebarMenuButton>
                {isWebsiteOpen && (
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === "/files"}
                      >
                        <Link to="/files" onClick={handleLinkClick}>
                          <span>Files</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive("/sites/domains")}
                      >
                        <Link to="/sites/domains" onClick={handleLinkClick}>
                          <span>Domains</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton 
                        asChild 
                        isActive={isActive("/pages")}
                      >
                        <Link to="/pages" onClick={handleLinkClick}>
                          <span>Pages</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={isActive("/support")}
                      >
                        <Link to="/support" onClick={handleLinkClick}>
                          <span>Support</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                {sessionLoading ? (
                  <SidebarMenuButton disabled tooltip="Loading">
                    <HelpCircle className="opacity-50" />
                    <span className="text-muted-foreground">…</span>
                  </SidebarMenuButton>
                ) : isSuperuser ? (
                  <SidebarMenuButton asChild tooltip="Admin" isActive={isActive("/admin")}>
                    <Link to="/admin" onClick={handleLinkClick}>
                      <Shield />
                      <span>Admin</span>
                    </Link>
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton asChild tooltip="Help">
                    <a href="mailto:hello@engram.sh" onClick={handleLinkClick}>
                      <HelpCircle />
                      <span>Help</span>
                    </a>
                  </SidebarMenuButton>
                )}
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="w-full">
              <div className="flex items-center justify-between w-full">
                <span className="truncate font-medium">
                  {sessionLoading ? "\u2026" : artistName}
                </span>
                <SidebarMenuButton asChild tooltip="Settings" isActive={isActive("/settings")} size="icon" className="h-8 w-8">
                  <Link to="/settings" onClick={handleLinkClick}>
                    <Settings className="h-4 w-4" />
                  </Link>
                </SidebarMenuButton>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
